/** Send and decision dialogs (also used for invoices). */
import { useEffect, useState, type ChangeEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { OfferResponse } from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import { InputText } from "@primereact/ui/inputtext";
import { Textarea } from "@primereact/ui/textarea";
import { apiGet } from "../../lib/api.js";
import { Field } from "../../components/ui/Field.js";
import { CheckboxField, Modal } from "../../components/ui/controls.js";
import { MutationError } from "../shared.js";

export function useOffer(id: string | undefined) {
  return useQuery({
    queryKey: ["offer", id],
    queryFn: () =>
      apiGet<{ data: OfferResponse }>(`/offers/${id}`).then(
        (response) => response.data,
      ),
    enabled: Boolean(id),
  });
}

export function SendDialog({
  open,
  onClose,
  defaultEmail,
  onSend,
  pending,
  error,
  resend,
}: {
  open: boolean;
  onClose: () => void;
  defaultEmail?: string;
  onSend: (input: { email?: string; deliver: boolean }) => void;
  pending: boolean;
  error: unknown;
  resend?: boolean;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [deliver, setDeliver] = useState(true);
  useEffect(() => {
    if (open) {
      setEmail(defaultEmail ?? "");
      setDeliver(true);
    }
  }, [open, defaultEmail]);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={resend ? t("doc.resendTitle") : t("doc.sendTitle")}
      footer={
        <>
          <Button variant="text" severity="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            loading={pending || undefined}
            disabled={deliver && !email.includes("@")}
            onClick={() =>
              onSend({ ...(deliver ? { email: email.trim() } : {}), deliver })
            }
          >
            {deliver ? t("doc.sendEmail") : t("doc.markSent")}
          </Button>
        </>
      }
    >
      {resend ? null : (
        <CheckboxField
          id="send-manual"
          checked={!deliver}
          onChange={(manual) => setDeliver(!manual)}
          label={t("doc.markSent")}
          hint={t("doc.markSentHint")}
        />
      )}
      {deliver ? (
        <Field label={t("common.email")} required>
          <InputText
            type="email"
            value={email}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setEmail(event.target.value)
            }
          />
        </Field>
      ) : null}
      <p className="text-sm text-app-muted">{t("doc.sendFreezes")}</p>
      <MutationError error={error} />
    </Modal>
  );
}

export function DecisionDialog({
  decision,
  onClose,
  onConfirm,
  pending,
  error,
}: {
  decision: "accept" | "decline" | null;
  onClose: () => void;
  onConfirm: (note: string) => void;
  pending: boolean;
  error: unknown;
}) {
  const { t } = useTranslation();
  const [note, setNote] = useState("");
  return (
    <Modal
      open={decision !== null}
      onClose={onClose}
      title={
        decision === "accept" ? t("offer.acceptTitle") : t("offer.declineTitle")
      }
      footer={
        <>
          <Button variant="text" severity="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            severity={decision === "decline" ? "danger" : undefined}
            loading={pending || undefined}
            onClick={() => onConfirm(note)}
          >
            {decision === "accept" ? t("offer.accept") : t("offer.decline")}
          </Button>
        </>
      }
    >
      <Field label={t("offer.decisionNote")}>
        <Textarea
          value={note}
          rows={3}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
            setNote(event.target.value)
          }
          placeholder={t("offer.decisionNotePlaceholder")}
        />
      </Field>
      <MutationError error={error} />
    </Modal>
  );
}
